/**
 * fileUpload.ts — helpers for the Explorer bulk uploader (2026-06-29).
 *
 * The Designer file Explorer gained an uploader (multi-file + recursive folder)
 * that writes dropped/picked files into the active project via the same
 * upsert PUT the editor's save uses. These pure helpers decide which files are
 * text-uploadable and derive each file's in-project path; they are unit-tested
 * independently of the React component + the network.
 */

// Extensions we treat as binary. The project file API stores a UTF-8 string,
// so reading a binary file as text would corrupt it — we skip these in v1 and
// report them to the user rather than silently writing garbage.
const BINARY_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'ico', 'tif', 'tiff', 'avif', 'heic', 'psd',
  'pdf', 'zip', 'gz', 'tgz', 'tar', 'rar', '7z', 'bz2', 'xz',
  'mp3', 'mp4', 'mov', 'avi', 'mkv', 'webm', 'wav', 'ogg', 'flac', 'm4a',
  'woff', 'woff2', 'ttf', 'otf', 'eot',
  'exe', 'dll', 'so', 'dylib', 'bin', 'class', 'jar', 'wasm', 'o', 'a',
  'sqlite', 'db', 'parquet',
]);

/**
 * True when a file should be uploaded as text. Extensionless files (README,
 * Dockerfile, LICENSE, .gitignore-style dotfiles) are treated as text.
 */
export function isProbablyTextFile(name: string): boolean {
  const base = name.slice(name.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  // No dot, or leading-dot dotfile with no other dot (".gitignore") → text.
  if (dot <= 0) return true;
  const ext = base.slice(dot + 1).toLowerCase();
  return !BINARY_EXTENSIONS.has(ext);
}

/**
 * The in-project path for an uploaded file. Folder uploads carry the chosen
 * folder's structure in `webkitRelativePath` (e.g. "docs/sub/note.md"); plain
 * multi-file uploads only have `name`. Leading "./" or "/" is stripped so the
 * path is always project-root-relative.
 */
export function relPathForUpload(file: { name: string; webkitRelativePath?: string }): string {
  const raw =
    file.webkitRelativePath && file.webkitRelativePath.length > 0
      ? file.webkitRelativePath
      : file.name;
  return raw.replace(/^\.?\/+/, '');
}

/**
 * Prefix an uploaded file's project-relative path with a target folder — the
 * folder a user right-clicked "Upload files here" on, or dropped OS files onto
 * (2026-07-11). Leading/trailing slashes on the folder and a leading slash on
 * the file path are normalized so the result is always clean and
 * project-root-relative. An empty/`/` target dir means the project root
 * (unchanged root-upload behavior).
 */
export function joinUploadPath(targetDir: string, relPath: string): string {
  const dir = targetDir.replace(/^\/+|\/+$/g, '');
  const rel = relPath.replace(/^\/+/, '');
  return dir ? `${dir}/${rel}` : rel;
}

/**
 * Reject paths that try to escape the project root via `..` segments. The
 * browser never produces these for picked files, but a drag-drop DataTransfer
 * or a crafted input could — fail closed.
 */
export function isSafeUploadPath(path: string): boolean {
  const clean = relPathForUpload({ name: path });
  if (clean.length === 0) return false;
  return !clean.split('/').some((seg) => seg === '..' || seg === '');
}
